import * as React from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Dialog, DialogContent, DialogTitle, Typography, useMediaQuery, useTheme } from "@mui/material";
import config from "../../config";
import axiosInstant from "../../api";
import { createProductForBrand } from "../../api/endpoints";
import BrandForm, { SaveChangesParams } from "./BrandForm";
import { IProduct } from "./components/ProductList";
import { handleConflictError } from "../../utils/errorHandler";
import { IconCross } from "../../utils/icons";
import { Brand } from "../videoProduction/components/constants";

interface Props {
  onClose: () => void;
  onSuccess: (brand: Brand) => void;
}

export default function CreateBrand({ onClose, onSuccess }: Props) {
  const navigate = useNavigate();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const createProduct = async (brandId: string, product: IProduct) => {
    const payload: any = {
      name: product.title,
      brandId,
      description: product.description,
      image: product.image || undefined,
    };

    const endpoint = createProductForBrand(brandId.toString());
    try {
      const { data } = await axiosInstant.post(endpoint, payload);
      if (data?.desireid) return data.desireid as string;
    } catch (error) {
      toast.error((error as Error).message);
    }
    return null;
  };

  // Handle General Form Changes
  const handleSaveChanges: SaveChangesParams = async (
    payload,
    productIds,
    newProducts,
    prosIds,
  ) => {
    try {
      const url = `${config.API_URL}/brands/create`;
      const { data } = await axiosInstant.post(url, payload);

      if (data?.id as Brand) {
        const brandId = data.id;
        // Only AMs can attach experts or products, so these are sent only
        // when something was picked (sales creates brands too).
        if (prosIds.length) {
          await axiosInstant.post(`/brands/${brandId}/pros`, {
            user_ids: prosIds,
          });
        }

        const newProductPromises = [];
        // eslint-disable-next-line no-restricted-syntax
        for (const newProduct of newProducts) {
          if (newProduct.title)
            newProductPromises.push(createProduct(brandId, newProduct));
        }
        const newProductIds = (await Promise.all(newProductPromises)).filter(
          (e) => !!e,
        );

        const desireIds = [...newProductIds, ...productIds];
        if (desireIds.length) {
          await axiosInstant.post(`/brands/${brandId}/desires`, {
            desire_ids: desireIds,
          });
        }

        toast.success("Brand created successfully!");
        onSuccess(data);
      }
    } catch (error: any) {
      if (!handleConflictError(error)) {
        toast.error("Failed to create brand. Please try again.");
      }
    }
  };

  return (
    <Dialog open fullWidth maxWidth="md" onClose={onClose} fullScreen={fullScreen}>
      <DialogTitle className="!pb-0 flex items-center justify-between">
        <Typography variant="h5">Add Brand</Typography>
        {/* stroke via currentColor — SVG presentation attributes can't read var() */}
        <div
          onClick={onClose}
          className="cursor-pointer hover:bg-surface-2 rounded-full p-1 text-text-1"
        >
          <IconCross height={18} width={18} stroke="currentColor" />
        </div>
      </DialogTitle>
      <DialogContent className="!p-0 bg-surface">
        <BrandForm saveChanges={handleSaveChanges} isDialog />
      </DialogContent>
    </Dialog>
  );
}
